import { logger } from "@/services/logger";
import type { ChatMessage, ChatParams, ModelInfo } from "@/types";
import { AIServiceError, type ChatStreamDelta, type StreamChatRequest } from "../../types";

/**
 * Raw fetch layer for Ollama's local REST API. Every Ollama-shaped payload is
 * mapped to a domain type before leaving this file — nothing outside
 * `providers/ollama` should know what Ollama's JSON looks like.
 */
const log = logger.scope("ollama");

const REQUEST_TIMEOUT_MS = 10_000;
/** Generous — a cold model load can legitimately take a while before the
 * first token arrives. Once streaming starts there is no further timeout;
 * only the user's Stop button or natural completion ends it. */
const CHAT_CONNECT_TIMEOUT_MS = 30_000;

interface OllamaTagsModel {
  name: string;
  modified_at: string;
  size: number;
  details?: { parameter_size?: string; families?: string[] };
}

interface OllamaTagsResponse {
  models?: OllamaTagsModel[];
}

interface OllamaChatStreamLine {
  message?: { role: string; content: string };
  done: boolean;
  error?: string;
}

interface OllamaGenerateResponse {
  response?: string;
}

function timeoutSignal(ms: number): AbortSignal {
  const controller = new AbortController();
  setTimeout(() => controller.abort(), ms);
  return controller.signal;
}

/** Merge multiple abort signals into one that fires when any of them do. */
function combineSignals(...signals: (AbortSignal | undefined)[]): AbortSignal | undefined {
  const active = signals.filter((s): s is AbortSignal => Boolean(s));
  if (active.length === 0) return undefined;
  if (active.length === 1) return active[0];

  const controller = new AbortController();
  for (const s of active) {
    if (s.aborted) {
      controller.abort();
      break;
    }
    s.addEventListener("abort", () => controller.abort(), { once: true });
  }
  return controller.signal;
}

function mapFetchError(err: unknown, userAborted: boolean): AIServiceError {
  if (userAborted || (err instanceof DOMException && err.name === "AbortError")) {
    return new AIServiceError("aborted", "Generation stopped.");
  }
  if (err instanceof TypeError) {
    return new AIServiceError(
      "network",
      "Couldn't reach Ollama. Make sure it's installed and running on this device.",
      { cause: err },
    );
  }
  return new AIServiceError(
    "unknown",
    err instanceof Error ? err.message : "Something went wrong talking to Ollama.",
    { cause: err },
  );
}

function toOllamaOptions(params: ChatParams) {
  return {
    temperature: params.temperature,
    top_p: params.topP,
    num_ctx: params.numCtx,
    num_predict: params.numPredict,
  };
}

function toOllamaMessages(messages: ChatMessage[]) {
  return messages
    .filter((m) => m.status !== "error")
    .map((m) => ({
      role: m.role,
      content: m.content,
      // Ollama's `/api/chat` accepts a per-message `images` field (base64,
      // no data-URI prefix) — omitted entirely for text-only messages so
      // non-vision models never see the field.
      ...(m.images && m.images.length > 0 ? { images: m.images.map((img) => img.base64) } : {}),
    }));
}

export async function listModels(baseUrl: string): Promise<ModelInfo[]> {
  let response: Response;
  try {
    response = await fetch(`${baseUrl}/api/tags`, { signal: timeoutSignal(REQUEST_TIMEOUT_MS) });
  } catch (err) {
    throw mapFetchError(err, false);
  }

  if (!response.ok) {
    throw new AIServiceError("network", `Ollama responded with status ${response.status}.`);
  }

  let data: OllamaTagsResponse;
  try {
    data = (await response.json()) as OllamaTagsResponse;
  } catch (err) {
    throw new AIServiceError("invalid-response", "Ollama returned an unreadable response.", {
      cause: err,
    });
  }

  return (data.models ?? []).map((m) => ({
    id: m.name,
    name: m.name,
    paramSize: m.details?.parameter_size,
    sizeBytes: m.size,
    modifiedAt: Date.parse(m.modified_at) || Date.now(),
    available: true,
    // "clip" in `details.families` is how Ollama marks a multimodal model
    // (llava, moondream, bakllava, …); absent entirely on hosts that don't
    // report families, which callers treat as "unknown" via `?? undefined`.
    supportsVision: m.details?.families ? m.details.families.includes("clip") : undefined,
  }));
}

export async function* streamChat(request: StreamChatRequest): AsyncGenerator<ChatStreamDelta> {
  const { baseUrl, model, messages, params, signal } = request;
  const combined = combineSignals(signal, timeoutSignal(CHAT_CONNECT_TIMEOUT_MS));

  let response: Response;
  try {
    response = await fetch(`${baseUrl}/api/chat`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model,
        messages: toOllamaMessages(messages),
        stream: true,
        options: toOllamaOptions(params),
      }),
      signal: combined,
    });
  } catch (err) {
    throw mapFetchError(err, signal?.aborted ?? false);
  }

  if (response.status === 404) {
    throw new AIServiceError("model-not-found", `The model "${model}" isn't installed.`);
  }
  if (!response.ok || !response.body) {
    throw new AIServiceError("network", `Ollama responded with status ${response.status}.`);
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";

  try {
    while (true) {
      const { value, done } = await reader.read();
      if (done) return;

      buffer += decoder.decode(value, { stream: true });

      let newlineIndex = buffer.indexOf("\n");
      while (newlineIndex !== -1) {
        const line = buffer.slice(0, newlineIndex).trim();
        buffer = buffer.slice(newlineIndex + 1);

        if (line) {
          let parsed: OllamaChatStreamLine;
          try {
            parsed = JSON.parse(line) as OllamaChatStreamLine;
          } catch (err) {
            log.warn("Skipping malformed stream line from Ollama", { line, err });
            newlineIndex = buffer.indexOf("\n");
            continue;
          }

          if (parsed.error) {
            throw new AIServiceError("invalid-response", parsed.error);
          }

          yield { content: parsed.message?.content ?? "", done: parsed.done };
          if (parsed.done) return;
        }

        newlineIndex = buffer.indexOf("\n");
      }
    }
  } catch (err) {
    if (err instanceof AIServiceError) throw err;
    throw mapFetchError(err, signal?.aborted ?? false);
  } finally {
    reader.releaseLock();
  }
}

/** One-shot, non-streaming request used only for auto-generating a short
 * conversation title. Never throws — callers fall back to a heuristic title
 * on `null`. */
export async function generateTitle(
  baseUrl: string,
  model: string,
  seedText: string,
): Promise<string | null> {
  try {
    const response = await fetch(`${baseUrl}/api/generate`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        model,
        prompt: `Write a short 3-6 word title (no quotes, no trailing punctuation) for a chat that starts with:\n\n"${seedText.slice(0, 500)}"`,
        stream: false,
        options: { temperature: 0.3, num_predict: 16 },
      }),
      signal: timeoutSignal(REQUEST_TIMEOUT_MS),
    });
    if (!response.ok) return null;

    const data = (await response.json()) as OllamaGenerateResponse;
    const title = data.response?.trim().replace(/^["']|["']$/g, "").trim();
    return title && title.length > 0 ? title : null;
  } catch (err) {
    log.warn("Title generation failed; caller will fall back to a heuristic title", err);
    return null;
  }
}

/** Quick reachability ping used by the browser-dev fallback (outside Tauri,
 * where the native install/running checks aren't available). */
export async function pingVersion(baseUrl: string): Promise<string | null> {
  try {
    const response = await fetch(`${baseUrl}/api/version`, {
      signal: timeoutSignal(REQUEST_TIMEOUT_MS),
    });
    if (!response.ok) return null;
    const data = (await response.json()) as { version?: string };
    return data.version ?? null;
  } catch {
    return null;
  }
}
